import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getToken } from './tokenHelper';
import { getApiBaseUrl } from './apiConfig';

// Global logout callback - will be set by AuthContext
let globalLogoutCallback: (() => void) | null = null;

/**
 * Set the global logout callback function
 * This should be called from AuthContext when it initializes
 * Also sets up the interceptor on the default axios instance for direct axios calls
 */
export const setLogoutCallback = (logoutFn: () => void) => {
  globalLogoutCallback = logoutFn;
  // Also setup interceptor on default axios instance for direct axios calls in components
  setupAuthErrorInterceptor(axios);
};

/**
 * Setup response interceptor to handle 401 errors and token expiration
 * This will automatically logout user and redirect to login on auth errors
 */
export const setupAuthErrorInterceptor = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Check if it's a 401 Unauthorized error
      if (error.response?.status === 401) {
        // Check if it's a token expiration error
        const errorMessage = error.response?.data?.message || error.message || '';
        const isTokenExpired = 
          errorMessage.includes('TokenExpiredError') ||
          errorMessage.includes('jwt expired') ||
          errorMessage.includes('token expired') ||
          errorMessage.includes('Invalid token') ||
          errorMessage.includes('Access denied');

        if (isTokenExpired && globalLogoutCallback) {
          console.warn('Token expired or invalid. Logging out user...');
          // Clear token and user data, then redirect
          globalLogoutCallback();
          
          // Redirect to login page
          if (typeof window !== 'undefined') {
            window.location.href = '/auth';
          }
        }
      }

      // Return the error so it can still be handled by the calling code if needed
      return Promise.reject(error);
    }
  );
};

/**
 * Create a configured axios instance with auth interceptors
 * This can be used by service files instead of creating their own instances
 */
export const createApiClient = (baseURL?: string): AxiosInstance => {
  let API_BASE_URL = baseURL || getApiBaseUrl();
  
  // Don't strip /api - it's intentionally added by service files that need it
  // Some services (like distributionApi) explicitly add /api to hit the correct endpoints

  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  // Request interceptor: Add auth token to requests
  apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor: Handle 401 errors and token expiration
  setupAuthErrorInterceptor(apiClient);

  return apiClient;
};

/**
 * Setup auth error interceptor for an existing axios instance
 * Use this if you already have an axios instance and just want to add the interceptor
 */
export const addAuthErrorInterceptor = (axiosInstance: AxiosInstance) => {
  setupAuthErrorInterceptor(axiosInstance);
};

